import { useEffect, useRef } from 'react';
import { useCountdown } from '../../hooks/useCountdown';
import { useStore } from '../../data/store';

export default function Hero() {
  const event = useStore((s) => s.event);
  const canvasRef = useRef(null);
  const countdownTarget = event?.dateStart
    ? `${event.dateStart}T${event.timeStart || '14:00'}:00+02:00`
    : '2026-09-05T14:00:00+02:00';
  const countdown = useCountdown(countdownTarget);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    class Spark {
      constructor() {
        this.reset();
      }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = canvas.height + 10;
        this.vx = (Math.random() - 0.5) * 1.5;
        this.vy = -(Math.random() * 2 + 1);
        this.life = Math.random() * 100 + 50;
        this.maxLife = this.life;
        this.size = Math.random() * 3 + 1;
        this.color = Math.random() > 0.5 ? '#1a5fb4' : '#3a8fd8';
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.life--;
        if (this.life <= 0) this.reset();
      }
      draw() {
        const alpha = (this.life / this.maxLife) * 0.4;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color.replace(')', `, ${alpha})`).replace('rgb', 'rgba').replace('#1a5fb4', `rgba(26,95,180,${alpha})`).replace('#3a8fd8', `rgba(58,143,216,${alpha})`);
        ctx.globalAlpha = alpha;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    for (let i = 0; i < 25; i++) {
      const s = new Spark();
      s.y = Math.random() * canvas.height;
      particles.push(s);
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => { p.update(); p.draw(); });
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const pad = (n) => String(n).padStart(2, '0');

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const lineupLive = event?.lineupPublished;

  const formatEventDate = () => {
    if (!event?.dateStart) return '5. & 6. SEPTEMBER 2026';
    const start = new Date(event.dateStart + 'T00:00:00');
    const end = event.dateEnd ? new Date(event.dateEnd + 'T00:00:00') : null;
    const month = start.toLocaleDateString('de-DE', { month: 'long' }).toUpperCase();
    const year = start.getFullYear();
    if (end && end.getTime() !== start.getTime()) {
      return `${start.getDate()}. & ${end.getDate()}. ${month} ${year}`;
    }
    return `${start.getDate()}. ${month} ${year}`;
  };

  return (
    <section className="relative flex items-center justify-center overflow-hidden" style={{ minHeight: '100svh' }}>
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: event?.heroImage
            ? `url(${event.heroImage})`
            : 'url(https://www.blick-aktuell.de/Bilder/Entspannte-Atmosphaere-direkt-am-Rhein-Fotos-privat-475979.jpg)',
          transform: 'translateZ(0)',
        }}
      />
      <div className="hero-gradient absolute inset-0" />
      <div className="noise-overlay" />
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />

      <div className="relative z-10 text-center px-5 max-w-5xl mx-auto w-full flex flex-col items-center">
        <p className="font-heading text-text-muted text-[10px] md:text-base tracking-[2px] md:tracking-[4px] mb-4 md:mb-6">
          BAD HÖNNINGEN AM RHEIN • EINTRITT FREI
        </p>

        <h1 className="font-heading leading-[0.9] text-text-primary mb-4 font-heading" style={{ fontSize: 'clamp(52px, 15vw, 140px)' }}>
          ROCK<span className="text-accent-red">N</span>RHEIN
        </h1>

        <p className="font-heading text-accent-red mb-4 md:mb-6" style={{ fontSize: 'clamp(22px, 6vw, 48px)' }}>
          {formatEventDate()}
        </p>

        <p className="font-body text-text-muted text-sm md:text-lg mb-10 md:mb-14 max-w-[280px] md:max-w-xl leading-relaxed text-center">
          {event?.tagline}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-10 max-w-[300px] sm:max-w-full w-full mb-12 md:mb-16">
          {[
            { value: countdown.days, label: 'TAGE' },
            { value: countdown.hours, label: 'STUNDEN' },
            { value: countdown.minutes, label: 'MINUTEN' },
            { value: countdown.seconds, label: 'SEKUNDEN' },
          ].map((item) => (
            <div key={item.label} className="text-center border-t-2 border-accent-red pt-3">
              <div className="font-heading text-accent-red" style={{ fontSize: 'clamp(48px, 12vw, 96px)' }}>
                {pad(item.value)}
              </div>
              <div className="font-heading text-[9px] sm:text-sm text-text-muted tracking-[3px] mt-1">
                {item.label}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
          {lineupLive ? (
            <a
              href="#lineup"
              onClick={scrollTo('#lineup')}
              className="w-full sm:w-auto max-w-[280px] bg-accent-red text-white font-heading text-base tracking-wider px-8 flex items-center justify-center border-2 border-accent-red hover:bg-transparent hover:text-accent-red transition-all"
              style={{ height: '52px' }}
            >
              LINEUP ANSEHEN
            </a>
          ) : (
            <a
              href="#newsletter"
              onClick={scrollTo('#newsletter')}
              className="w-full sm:w-auto max-w-[280px] bg-accent-red text-white font-heading text-base tracking-wider px-8 flex items-center justify-center border-2 border-accent-red hover:bg-transparent hover:text-accent-red transition-all"
              style={{ height: '52px' }}
            >
              LINEUP-UPDATE BEKOMMEN
            </a>
          )}
          <a
            href="#infos"
            onClick={scrollTo('#infos')}
            className="text-text-muted hover:text-text-primary transition-colors font-body text-[13px] mt-2 sm:mt-0"
          >
            ↓ Infos & Anreise
          </a>
        </div>
      </div>
    </section>
  );
}
